import isNotNullish from "lib/isNotNullish";
import { pluck } from "lib/Array";
import { Maybe } from "lib/utilityTypes";

type GenericNode = { id: string };
type GenericEdge<TNode extends GenericNode> = Maybe<{ node?: Maybe<TNode> }>;
type GenericConnection<TNode extends GenericNode> = Maybe<{
  edges?: Maybe<GenericEdge<TNode>[]>;
}>;

export const getNodesFromEdges = <TNode extends GenericNode>(
  edges?: Maybe<GenericEdge<TNode>[]>
): TNode[] => (edges ?? []).map((edge) => edge?.node).filter(isNotNullish);

const pluckFromEdges = <TNode extends GenericNode, TProp extends keyof TNode>(
  edges: Maybe<GenericEdge<TNode>[]>,
  key: TProp
) => pluck(getNodesFromEdges(edges), key);

export const getIdsFromEdges = <TNode extends GenericNode>(
  edges: Maybe<GenericEdge<TNode>[]>
) => pluckFromEdges(edges, "id");

export const getNodesFromConnection = <TNode extends GenericNode>(
  connection?: Maybe<GenericConnection<TNode>>
) => getNodesFromEdges(connection?.edges);

export const pluckFromConnection = <
  TNode extends GenericNode,
  TProp extends keyof TNode
>(
  connection: Maybe<GenericConnection<TNode>>,
  key: TProp
) => pluckFromEdges(connection?.edges, key);

export const getIdsFromConnection = <TNode extends GenericNode>(
  connection?: Maybe<GenericConnection<TNode>>
) => pluckFromConnection(connection, "id");

type GlobalIdType =
  | "Account"
  | "Assortment"
  | "Color"
  | "Conversation"
  | "LegacyItemId"
  | "RetailWorkspace"
  | "Retailer"
  | "Size"
  | "Sku"
  | "User"
  | "AssortmentSKUColorDoorQuantity";

type GlobalId = {
  type: GlobalIdType;
  id: string;
};

export const toGlobalId = ({ type, id }: GlobalId): string => {
  if (!type || !id) {
    throw new Error(`Unable to encode global id: "${type}:${id}"`);
  }
  return window.btoa(`${type}:${id}`);
};

export const fromGlobalId = (guid: string): GlobalId => {
  if (!guid) {
    throw new Error(`Unable to decode global id: "${guid}"`);
  }
  const [type, id] = window.atob(guid).split(":");
  return { type: type as GlobalIdType, id };
};

export const decodeNodeId = <TNode extends GenericNode>(
  node: TNode
): TNode => ({
  ...node,
  id: fromGlobalId(node.id).id,
});
